import { Lightning } from '@lightningjs/sdk'
import lottie, {
  AnimationEventCallback,
  AnimationItem,
} from 'lottie-web/build/player/lottie_light_canvas'

import Loader from './loader.json'

export interface ILottieDocument {
  v: string
  meta: object
  fr: number
  nm: string
  w: number
  h: number
}

// This component consists on create a canvas, load the lottie animation inside it
// and then use Lightning to return said canvas as a texture, basically this:
// https://lightningjs.io/docs/#/lightning-core-reference/RenderEngine/Textures/Canvas?id=canvas
export default class LottieAnimation extends Lightning.Component {
  private _canvas: HTMLCanvasElement | undefined
  private readonly _data: ILottieDocument | undefined
  private _animation: AnimationItem | undefined
  private _width = 0
  private _height = 0
  private _lastFrame = -1
  private _onLottieFrame: AnimationEventCallback

  set width(value: number) {
    this._width = value
    if (this._canvas) this._canvas.width = value
  }

  set height(value: number) {
    this._height = value
    if (this._canvas) this._canvas.height = value
  }

  _init() {
    this._onLottieFrame = this.onLottieFrame.bind(this)
  }

  _active() {
    if (this._animation) {
      this._animation.removeEventListener('drawnFrame', this._onLottieFrame)
      this._animation.destroy()
    }

    // create a hidden canvas for Lottie
    this._canvas = document.createElement('canvas')
    this.w = this._canvas.width = this._width
    this.h = this._canvas.height = this._height

    // create Lottie animator, providing the canvas's 2d context
    this._animation = lottie.loadAnimation({
      container: null, // undocumented: container is optional
      animationData: Loader,
      renderer: 'canvas',
      rendererSettings: {
        context: this._canvas.getContext('2d'),
      },
      autoplay: true,
      loop: true,
    })

    // listen to Lottie renders to update the texture
    this._animation.addEventListener('drawnFrame', this._onLottieFrame)
  }

  // cleanup on inactive
  _inactive() {
    if (this._animation) {
      this._animation.removeEventListener('drawnFrame', this._onLottieFrame)
      this._animation.destroy()
      this._animation = undefined
    }
    this._canvas = undefined
  }

  onLottieFrame() {
    if (!this._animation) return

    // calculate a Lightning cache key to avoid creating new textures
    const frame = Math.round(this._animation.currentFrame)

    if (frame === this._lastFrame) return

    this._lastFrame = frame
    const name = this._animation.name ?? this._data?.nm
    const key = `lottie-${name}-${frame}-${this._width}-${this._height}`

    this.texture = Lightning.Tools.getCanvasTexture(
      (cb) => this._canvas && cb(null, this._canvas),
      key,
    )
  }
}
